import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location, NavigateFunction } from 'react-router-dom'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { alterPlaylistGroups } from '@disco/state-management/actions/ExplorerActions'
import { createGroup } from '@disco/state-management/actions/GroupActions'
import { getGroup } from '@disco/state-management/selectors/groups'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { getUser } from '@disco/state-management/selectors/user'
import { sendPlaylistDownloadEventThrottled } from '@disco/utils/eventsApi'

import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { User } from 'types/user'

import type { RootState } from 'store/configureStore'

import GroupModal from 'components/common/custom-libraries/edit-form'
import DocumentHeadTags from 'components/document-head-tags'

const mapStateToProps = (
  state: RootState,
  ownProps: {
    params: { playlistId?: string }
    searchParams: URLSearchParams
  },
) => {
  const sourceGroupParam = ownProps.searchParams.get('source_group')
  return {
    playlist: ownProps.params.playlistId
      ? getPlaylist(state, {
          id: parseInt(ownProps.params.playlistId, 10),
        })
      : null,
    sourceGroup: sourceGroupParam
      ? getGroup(state, {
          groupId: parseInt(sourceGroupParam, 10),
        })
      : null,
    user: getUser(state),
  }
}

const mapDispatchToProps = {
  createGroup,
  sendMixpanelEvent,
  alterPlaylistGroups,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  navigate: NavigateFunction
}

class AddToNewChannel extends React.Component<Props> {
  _onCreateGroup = (name: string, group: Group) => {
    if (group && this.props.playlist) {
      this.props.alterPlaylistGroups([this.props.playlist], [group], [])
      this.props.sendMixpanelEvent('Add to new channel from playlist menu')
    }

    if (
      this.props.playlist &&
      this.props.sourceGroup &&
      INBOX_TYPES.includes(this.props.sourceGroup.group_type)
    ) {
      sendPlaylistDownloadEventThrottled(
        this.props.playlist.playlist_id,
        undefined,
        this.props.sourceGroup.id,
      )
    }

    this._closeModal()
  }
  _closeModal = () => this.props.navigate('/')

  render() {
    return (
      <div>
        <DocumentHeadTags title="Add to new channel | DISCO" />
        <GroupModal
          user={this.props.user}
          createGroup={this.props.createGroup}
          groupType={CUSTOM_CHANNEL}
          onSubmit={this._onCreateGroup}
          onClose={this._closeModal}
        />
      </div>
    )
  }
}

const ConnectedAddToNewChannel = connector(AddToNewChannel)

function AddToNewChannelWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ playlistId: string }>()
  const [searchParams] = useSearchParams()
  return (
    <ConnectedAddToNewChannel
      navigate={navigate}
      params={params}
      searchParams={searchParams}
    />
  )
}

export default AddToNewChannelWithRouter
